import { useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from '../store';
import { useShareUrl } from '../hooks/useShareUrl';

const SANDBOX_FLAGS = [
  'allow-scripts',
  'allow-forms',
  'allow-same-origin',
  'allow-popups',
  'allow-modals',
  'allow-top-navigation',
] as const;
type SandboxFlag = (typeof SANDBOX_FLAGS)[number];

const REFERRER_OPTIONS = [
  'no-referrer',
  'no-referrer-when-downgrade',
  'origin',
  'origin-when-cross-origin',
  'same-origin',
  'strict-origin',
  'strict-origin-when-cross-origin',
  'unsafe-url',
] as const;
type ReferrerPolicy = (typeof REFERRER_OPTIONS)[number];

export function PreviewPane() {
  const previewOpen = useStore((s) => s.previewOpen);
  const setPreviewOpen = useStore((s) => s.setPreviewOpen);
  const shareUrl = useShareUrl();

  const [sandboxOn, setSandboxOn] = useState(false);
  const [sandboxFlags, setSandboxFlags] = useState<SandboxFlag[]>([
    'allow-scripts',
    'allow-forms',
  ]);
  const [credentialless, setCredentialless] = useState(false);
  const [referrerPolicy, setReferrerPolicy] = useState<ReferrerPolicy>('no-referrer');
  const [allow, setAllow] = useState('');
  const [autoReload, setAutoReload] = useState(true);
  const [iframeUrl, setIframeUrl] = useState(shareUrl);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Debounced auto-reload: when shareUrl changes, wait briefly before
  // pointing the iframe at the new URL so we don't thrash on every keystroke.
  useEffect(() => {
    if (!previewOpen) return;
    if (!autoReload) return;
    const t = window.setTimeout(() => setIframeUrl(shareUrl), 400);
    return () => window.clearTimeout(t);
  }, [shareUrl, previewOpen, autoReload]);

  const sandboxValue = useMemo(() => {
    if (!sandboxOn) return undefined;
    return sandboxFlags.join(' ');
  }, [sandboxOn, sandboxFlags]);

  const refresh = () => {
    setIframeUrl((cur) => (cur === shareUrl ? `${shareUrl}#${Date.now()}` : shareUrl));
  };

  const toggleFlag = (flag: SandboxFlag) =>
    setSandboxFlags((cur) =>
      cur.includes(flag) ? cur.filter((f) => f !== flag) : [...cur, flag],
    );

  if (!previewOpen) return null;

  // iframe attributes that React ignores when undefined; we have to spread to
  // omit them entirely (not pass empty strings).
  const iframeProps: Record<string, string> = {};
  if (sandboxValue !== undefined) iframeProps.sandbox = sandboxValue;
  if (allow.trim()) iframeProps.allow = allow.trim();
  if (credentialless) iframeProps.credentialless = '';

  return (
    <aside className="preview-pane" aria-label="Preview">
      <header className="preview-head">
        <strong>Preview</strong>
        <span className="preview-url" title={iframeUrl}>{iframeUrl}</span>
        <button type="button" onClick={refresh} title="Reload preview">
          ↻
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(false)}
          className="preview-close"
          aria-label="Close preview"
          title="Close"
        >
          ×
        </button>
      </header>

      <details className="preview-controls">
        <summary>iframe attributes</summary>
        <div className="preview-controls-grid">
          <label className="preview-toggle">
            <input
              type="checkbox"
              checked={autoReload}
              onChange={(e) => setAutoReload(e.target.checked)}
            />
            <span>Auto-reload on edit</span>
          </label>
          <label className="preview-toggle">
            <input
              type="checkbox"
              checked={sandboxOn}
              onChange={(e) => setSandboxOn(e.target.checked)}
            />
            <span>
              <code>sandbox</code> (restrict)
            </span>
          </label>
          {sandboxOn && (
            <div className="preview-sandbox-flags">
              {SANDBOX_FLAGS.map((f) => (
                <label key={f} className="preview-toggle small">
                  <input
                    type="checkbox"
                    checked={sandboxFlags.includes(f)}
                    onChange={() => toggleFlag(f)}
                  />
                  <span>{f}</span>
                </label>
              ))}
            </div>
          )}
          <label className="preview-toggle">
            <input
              type="checkbox"
              checked={credentialless}
              onChange={(e) => setCredentialless(e.target.checked)}
            />
            <span>
              <code>credentialless</code>
            </span>
          </label>
          <label className="preview-field">
            <span>
              <code>referrerpolicy</code>
            </span>
            <select
              value={referrerPolicy}
              onChange={(e) => setReferrerPolicy(e.target.value as ReferrerPolicy)}
            >
              {REFERRER_OPTIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="preview-field">
            <span>
              <code>allow</code>
            </span>
            <input
              type="text"
              placeholder="camera; microphone; clipboard-read"
              value={allow}
              onChange={(e) => setAllow(e.target.value)}
            />
          </label>
        </div>
        <p className="preview-hint">
          Use the topbar Domain switcher to load the preview from a different host
          for cross-origin tests.
        </p>
      </details>

      <div className="preview-frame-wrap">
        <iframe
          ref={iframeRef}
          key={iframeUrl}
          src={iframeUrl}
          referrerPolicy={referrerPolicy}
          title="Preview"
          {...iframeProps}
        />
      </div>
    </aside>
  );
}
